import React, { useState, useEffect, useMemo } from "react";
import PageHeader from "../../core/common/header/PageHeader";
import {
  FaUsers,
  FaChartBar,
  FaChartLine,
  FaCheckCircle,
  FaExclamationTriangle,
  FaBook,
  FaTasks,
  FaQuestionCircle,
  FaUserCheck,
  FaGraduationCap,
  FaPen,
  FaUserTie,
  FaHome,
  FaSync,
  FaTable,
  FaThLarge,
  FaChevronDown,
  FaSearch,
} from "react-icons/fa";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// Types
interface AdminBatchOverview {
  batchId: number;
  batchName: string;
  topic: string;
  mentorName: string;
  totalStudents: number;
  averageProgress: number;
  studentsAbove80Percent: number;
  studentsBelow50Percent: number;
}

interface AdminStudentProgress {
  studentId: number;
  studentName: string;
  studentEmail: string;
  overallProgress: number;
  chaptersProgress: number;
  homeworkProgress: number;
  quizProgress: number;
  writingTaskProgress: number;
  attendanceProgress: number;
  examCompleted: boolean;
  chaptersCompleted: number;
  chaptersTotal: number;
  homeworkCompleted: number;
  homeworkTotal: number;
  quizzesCompleted: number;
  quizzesTotal: number;
  writingTasksCompleted: number;
  writingTasksTotal: number;
  classesAttended: number;
  classesTotal: number;
}

interface AdminBatchDetail {
  batchId: number;
  batchName: string;
  topic: string;
  mentorName: string;
  totalStudents: number;
  averageProgress: number;
  studentsAbove80Percent: number;
  studentsBelow50Percent: number;
  students: AdminStudentProgress[];
}

const AdminProgressDashboard: React.FC = () => {
  // Batch overviews (lightweight - just summaries)
  const [batchOverviews, setBatchOverviews] = useState<AdminBatchOverview[]>(
    []
  );

  // Selected batch (heavy - full student details)
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [selectedBatchDetail, setSelectedBatchDetail] =
    useState<AdminBatchDetail | null>(null);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // UI states
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<"cards" | "table">("cards");
  const [searchTerm, setSearchTerm] = useState("");

  // ✅ Fetch only lightweight batch summaries
  const fetchBatchOverviews = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${API_BASE}/api/admin/batches/progress-overview`,
        {
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch batch progress overview");
      }

      const data = await response.json();
      setBatchOverviews(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch full details only for selected batch
  const fetchBatchDetail = async (batchId: number) => {
    try {
      setDetailLoading(true);
      setSelectedBatchId(batchId);

      const response = await fetch(
        `${API_BASE}/api/admin/batch/${batchId}/progress-detail`,
        {
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch batch details");
      }

      const data = await response.json();
      setSelectedBatchDetail(data);
    } catch (err) {
      console.error("Error fetching batch detail:", err);
      setError("Failed to load batch details");
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchBatchOverviews();
    if (selectedBatchId) {
      await fetchBatchDetail(selectedBatchId);
    }
    setRefreshing(false);
  };

  useEffect(() => {
    fetchBatchOverviews();
  }, []);

  // ✅ Automatically select first batch after loading
  useEffect(() => {
    if (batchOverviews.length > 0 && !selectedBatchId) {
      fetchBatchDetail(batchOverviews[0].batchId);
    }
  }, [batchOverviews]);

  // ✅ Handle batch selection change
  const handleBatchSelect = (batchId: number) => {
    if (batchId === selectedBatchId) return; // Already selected
    fetchBatchDetail(batchId);
  };

  // ✅ Filtered batches for dropdown search
  const filteredBatches = useMemo(() => {
    if (!searchTerm) return batchOverviews;

    const term = searchTerm.toLowerCase();
    return batchOverviews.filter(
      (batch) =>
        batch.batchName.toLowerCase().includes(term) ||
        batch.topic.toLowerCase().includes(term) ||
        batch.mentorName.toLowerCase().includes(term)
    );
  }, [batchOverviews, searchTerm]);

  // ✅ Get currently selected batch overview
  const selectedBatchOverview = useMemo(() => {
    return batchOverviews.find((b) => b.batchId === selectedBatchId);
  }, [batchOverviews, selectedBatchId]);

  // Helper functions
  const getProgressColor = (percentage: number) => {
    if (percentage >= 80) return "bg-green-500";
    if (percentage >= 60) return "bg-yellow-500";
    if (percentage >= 40) return "bg-orange-500";
    return "bg-red-500";
  };

  const getProgressTextColor = (percentage: number) => {
    if (percentage >= 80) return "text-green-600";
    if (percentage >= 60) return "text-yellow-600";
    if (percentage >= 40) return "text-orange-600";
    return "text-red-600";
  };

  const getStudentCardColor = (progress: number) => {
    if (progress >= 80)
      return "bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200";
    if (progress >= 60)
      return "bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200";
    if (progress >= 40)
      return "bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200";
    return "bg-gradient-to-r from-rose-50 to-pink-50 border-rose-200";
  };

  const getStudentHeaderColor = (progress: number) => {
    if (progress >= 80) return "bg-emerald-400";
    if (progress >= 60) return "bg-blue-400";
    if (progress >= 40) return "bg-amber-400";
    return "bg-rose-400";
  };

  const getTableRowColor = (progress: number) => {
    if (progress >= 80) return "bg-emerald-50";
    if (progress >= 60) return "bg-blue-50";
    if (progress >= 40) return "bg-amber-50";
    return "bg-rose-50";
  };

  const formatProgress = (value: number) => {
    return Math.round(value * 10) / 10;
  };

  // ✅ Loading skeleton
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <PageHeader
          icon={<FaChartLine />}
          title="Batch Progress Dashboard"
          breadcrumb={[
            { label: "Home", to: "/" },
            { label: "Dashboard", to: "/admin/dashboard" },
            { label: "Progress Overview" },
          ]}
        />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-white rounded-lg shadow p-6 animate-pulse"
              >
                <div className="h-8 w-8 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                <div className="h-6 bg-gray-200 rounded w-12"></div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="h-10 bg-gray-200 rounded w-64 mb-4"></div>
            <div className="h-64 bg-gray-100 rounded"></div>
          </div>
        </main>
      </div>
    );
  }

  if (error && batchOverviews.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <FaExclamationTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Error Loading Dashboard
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchBatchOverviews}
            className="bg-[#009fe3] text-white px-4 py-2 rounded-md hover:bg-[#007cb5]"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaChartLine />}
        title="Batch Progress Dashboard"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Progress Overview" },
        ]}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Overall Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FaUsers className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Batches
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {batchOverviews.length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FaChartBar className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Avg Progress
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {batchOverviews.length > 0
                    ? formatProgress(
                        batchOverviews.reduce(
                          (sum, batch) => sum + batch.averageProgress,
                          0
                        ) / batchOverviews.length
                      )
                    : 0}
                  %
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FaCheckCircle className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  High Performing
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {
                    batchOverviews.filter(
                      (batch) => batch.averageProgress >= 80
                    ).length
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FaExclamationTriangle className="h-8 w-8 text-red-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Needs Attention
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {
                    batchOverviews.filter((batch) => batch.averageProgress < 60)
                      .length
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {batchOverviews.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <FaUsers className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Active Batches Found
            </h3>
            <p className="text-gray-600">
              There are no active batches to display.
            </p>
          </div>
        ) : (
          <>
            {/* ✅ BATCH SELECTOR DROPDOWN */}
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Select Batch to View Details
                </h3>

                {/* Search input */}
                <div className="relative mb-4">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search batches by name, topic, or mentor..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-[#009fe3] focus:border-[#009fe3]"
                  />
                </div>

                {/* Batch dropdown */}
                <div className="relative">
                  <select
                    value={selectedBatchId || ""}
                    onChange={(e) => handleBatchSelect(Number(e.target.value))}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-[#009fe3] text-gray-900 font-medium appearance-none cursor-pointer hover:border-[#009fe3] transition-colors"
                  >
                    {filteredBatches.map((batch) => (
                      <option key={batch.batchId} value={batch.batchId}>
                        {batch.batchName} - {batch.topic} (
                        {formatProgress(batch.averageProgress)}% avg,{" "}
                        {batch.totalStudents} students) - {batch.mentorName}
                      </option>
                    ))}
                  </select>
                  <FaChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Selected batch quick stats */}
              {selectedBatchOverview && (
                <div className="px-6 py-4 bg-gray-50">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="text-center">
                      <div className="text-sm text-gray-600 mb-1">
                        Avg Progress
                      </div>
                      <div
                        className={`text-2xl font-bold ${getProgressTextColor(
                          selectedBatchOverview.averageProgress
                        )}`}
                      >
                        {formatProgress(selectedBatchOverview.averageProgress)}%
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-600 mb-1">
                        Total Students
                      </div>
                      <div className="text-2xl font-bold text-blue-600">
                        {selectedBatchOverview.totalStudents}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-600 mb-1">
                        Above 80%
                      </div>
                      <div className="text-2xl font-bold text-green-600">
                        {selectedBatchOverview.studentsAbove80Percent}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-600 mb-1">
                        Below 50%
                      </div>
                      <div className="text-2xl font-bold text-red-600">
                        {selectedBatchOverview.studentsBelow50Percent}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-600 mb-1">Mentor</div>
                      <div className="text-sm font-semibold text-gray-900 flex items-center justify-center">
                        <FaUserTie className="mr-1 text-gray-500" />
                        {selectedBatchOverview.mentorName}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ✅ BATCH DETAILS */}
            {detailLoading ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <FaSync className="animate-spin h-12 w-12 text-[#009fe3] mx-auto mb-4" />
                <p className="text-gray-600">Loading batch details...</p>
              </div>
            ) : selectedBatchDetail ? (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                {/* Batch header */}
                <div className="px-6 py-4 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white">
                  <h3 className="text-lg font-medium">
                    {selectedBatchDetail.batchName} - Student Progress
                  </h3>
                  <p className="text-cyan-100 text-sm">
                    {selectedBatchDetail.topic} |{" "}
                    {selectedBatchDetail.students.length} students enrolled
                  </p>
                </div>

                <div className="p-6">
                  {/* View toggle */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-lg font-semibold text-gray-900">
                      Individual Student Progress
                    </div>

                    <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
                      <button
                        onClick={() => setViewMode("cards")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                          viewMode === "cards"
                            ? "bg-white text-[#009fe3] shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        <FaThLarge />
                        <span className="text-sm font-medium">Cards</span>
                      </button>
                      <button
                        onClick={() => setViewMode("table")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                          viewMode === "table"
                            ? "bg-white text-[#009fe3] shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        <FaTable />
                        <span className="text-sm font-medium">Table</span>
                      </button>
                    </div>
                  </div>

                  {/* CARDS VIEW */}
                  {viewMode === "cards" && (
                    <div className="space-y-6">
                      {selectedBatchDetail.students.map((student) => (
                        <div
                          key={student.studentId}
                          className={`border rounded-xl p-6 ${getStudentCardColor(
                            student.overallProgress
                          )} shadow-sm hover:shadow-md transition-shadow duration-200`}
                        >
                          {/* Student Header */}
                          <div className="relative">
                            <div
                              className={`absolute top-0 left-0 w-full h-1 ${getStudentHeaderColor(
                                student.overallProgress
                              )} rounded-t-lg`}
                            ></div>
                            <div className="flex items-center justify-between mb-6 pt-2">
                              <div>
                                <h5 className="font-semibold text-gray-900 text-lg">
                                  {student.studentName}
                                </h5>
                                <p className="text-sm text-gray-600">
                                  {student.studentEmail}
                                </p>
                              </div>
                              <div className="flex items-center space-x-3">
                                {student.examCompleted && (
                                  <span className="bg-emerald-100 text-emerald-800 text-xs font-semibold px-3 py-1 rounded-full flex items-center">
                                    <FaGraduationCap className="mr-1" />
                                    Exam Completed
                                  </span>
                                )}
                                <div
                                  className={`px-4 py-2 rounded-lg bg-white shadow-sm border-2 ${
                                    student.overallProgress >= 80
                                      ? "border-emerald-300"
                                      : student.overallProgress >= 60
                                      ? "border-blue-300"
                                      : student.overallProgress >= 40
                                      ? "border-amber-300"
                                      : "border-rose-300"
                                  }`}
                                >
                                  <span
                                    className={`text-xl font-bold ${getProgressTextColor(
                                      student.overallProgress
                                    )}`}
                                  >
                                    {formatProgress(student.overallProgress)}%
                                  </span>
                                  <div className="text-xs text-gray-500">
                                    Overall
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Overall Progress Bar */}
                          <div className="mb-6">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-700">
                                Overall Progress
                              </span>
                              <span
                                className={`text-sm font-semibold ${getProgressTextColor(
                                  student.overallProgress
                                )}`}
                              >
                                {formatProgress(student.overallProgress)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-4 shadow-inner">
                              <div
                                className={`h-4 rounded-full ${getProgressColor(
                                  student.overallProgress
                                )} shadow-sm transition-all duration-300`}
                                style={{
                                  width: `${student.overallProgress}%`,
                                }}
                              ></div>
                            </div>
                          </div>

                          {/* Component Progress Cards */}
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                            {/* Chapters */}
                            <div className="bg-sky-50 rounded-lg p-4 border border-sky-200">
                              <div className="flex items-center mb-2">
                                <FaBook className="h-4 w-4 text-sky-600 mr-2" />
                                <span className="text-sky-700 font-medium text-sm">
                                  Chapters
                                </span>
                              </div>
                              <div className="w-full bg-sky-200 rounded-full h-3 mb-2">
                                <div
                                  className="h-3 rounded-full bg-sky-500 transition-all duration-300"
                                  style={{
                                    width: `${student.chaptersProgress}%`,
                                  }}
                                ></div>
                              </div>
                              <div className="text-xs text-sky-600 font-medium">
                                {student.chaptersCompleted}/
                                {student.chaptersTotal}
                              </div>
                              <div className="text-sm font-semibold text-sky-700">
                                {formatProgress(student.chaptersProgress)}%
                              </div>
                            </div>

                            {/* Quizzes */}
                            <div className="bg-violet-50 rounded-lg p-4 border border-violet-200">
                              <div className="flex items-center mb-2">
                                <FaQuestionCircle className="h-4 w-4 text-violet-600 mr-2" />
                                <span className="text-violet-700 font-medium text-sm">
                                  Quizzes
                                </span>
                              </div>
                              <div className="w-full bg-violet-200 rounded-full h-3 mb-2">
                                <div
                                  className="h-3 rounded-full bg-violet-500 transition-all duration-300"
                                  style={{
                                    width: `${student.quizProgress}%`,
                                  }}
                                ></div>
                              </div>
                              <div className="text-xs text-violet-600 font-medium">
                                {student.quizzesCompleted}/
                                {student.quizzesTotal}
                              </div>
                              <div className="text-sm font-semibold text-violet-700">
                                {formatProgress(student.quizProgress)}%
                              </div>
                            </div>

                            {/* Homework */}
                            <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
                              <div className="flex items-center mb-2">
                                <FaTasks className="h-4 w-4 text-cyan-600 mr-2" />
                                <span className="text-cyan-700 font-medium text-sm">
                                  Homework
                                </span>
                              </div>
                              <div className="w-full bg-cyan-200 rounded-full h-3 mb-2">
                                <div
                                  className="h-3 rounded-full bg-cyan-500 transition-all duration-300"
                                  style={{
                                    width: `${student.homeworkProgress}%`,
                                  }}
                                ></div>
                              </div>
                              <div className="text-xs text-cyan-600 font-medium">
                                {student.homeworkCompleted}/
                                {student.homeworkTotal}
                              </div>
                              <div className="text-sm font-semibold text-cyan-700">
                                {formatProgress(student.homeworkProgress)}%
                              </div>
                            </div>

                            {/* Writing Tasks */}
                            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                              <div className="flex items-center mb-2">
                                <FaPen className="h-4 w-4 text-purple-600 mr-2" />
                                <span className="text-purple-700 font-medium text-sm">
                                  Writing
                                </span>
                              </div>
                              <div className="w-full bg-purple-200 rounded-full h-3 mb-2">
                                <div
                                  className="h-3 rounded-full bg-purple-500 transition-all duration-300"
                                  style={{
                                    width: `${student.writingTaskProgress}%`,
                                  }}
                                ></div>
                              </div>
                              <div className="text-xs text-purple-600 font-medium">
                                {student.writingTasksCompleted}/
                                {student.writingTasksTotal}
                              </div>
                              <div className="text-sm font-semibold text-purple-700">
                                {formatProgress(student.writingTaskProgress)}%
                              </div>
                            </div>

                            {/* Attendance */}
                            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                              <div className="flex items-center mb-2">
                                <FaUserCheck className="h-4 w-4 text-amber-600 mr-2" />
                                <span className="text-amber-700 font-medium text-sm">
                                  Attendance
                                </span>
                              </div>
                              <div className="w-full bg-amber-200 rounded-full h-3 mb-2">
                                <div
                                  className="h-3 rounded-full bg-amber-500 transition-all duration-300"
                                  style={{
                                    width: `${student.attendanceProgress}%`,
                                  }}
                                ></div>
                              </div>
                              <div className="text-xs text-amber-600 font-medium">
                                {student.classesAttended}/{student.classesTotal}
                              </div>
                              <div className="text-sm font-semibold text-amber-700">
                                {formatProgress(student.attendanceProgress)}%
                              </div>
                            </div>

                            {/* Exam */}
                            <div
                              className={`${
                                student.examCompleted
                                  ? "bg-emerald-50 border-emerald-200"
                                  : "bg-slate-50 border-slate-200"
                              } rounded-lg p-4 border`}
                            >
                              <div className="flex items-center mb-2">
                                <FaGraduationCap
                                  className={`h-4 w-4 mr-2 ${
                                    student.examCompleted
                                      ? "text-emerald-600"
                                      : "text-slate-400"
                                  }`}
                                />
                                <span
                                  className={`font-medium text-sm ${
                                    student.examCompleted
                                      ? "text-emerald-700"
                                      : "text-slate-600"
                                  }`}
                                >
                                  Exam
                                </span>
                              </div>
                              <div
                                className={`w-full rounded-full h-3 mb-2 ${
                                  student.examCompleted
                                    ? "bg-emerald-200"
                                    : "bg-slate-200"
                                }`}
                              >
                                <div
                                  className={`h-3 rounded-full transition-all duration-300 ${
                                    student.examCompleted
                                      ? "bg-emerald-500"
                                      : "bg-slate-400"
                                  }`}
                                  style={{
                                    width: `${
                                      student.examCompleted ? 100 : 0
                                    }%`,
                                  }}
                                ></div>
                              </div>
                              <div
                                className={`text-xs font-medium ${
                                  student.examCompleted
                                    ? "text-emerald-600"
                                    : "text-slate-500"
                                }`}
                              >
                                {student.examCompleted ? 1 : 0}/1
                              </div>
                              <div
                                className={`text-sm font-semibold ${
                                  student.examCompleted
                                    ? "text-emerald-700"
                                    : "text-slate-600"
                                }`}
                              >
                                {student.examCompleted ? "100%" : "0%"}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* TABLE VIEW */}
                  {viewMode === "table" && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                              Student
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Overall
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <div className="flex items-center justify-center">
                                <FaBook className="mr-1" />
                                Chapters
                              </div>
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <div className="flex items-center justify-center">
                                <FaQuestionCircle className="mr-1" />
                                Quizzes
                              </div>
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <div className="flex items-center justify-center">
                                <FaTasks className="mr-1" />
                                Homework
                              </div>
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <div className="flex items-center justify-center">
                                <FaPen className="mr-1" />
                                Writing
                              </div>
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <div className="flex items-center justify-center">
                                <FaUserCheck className="mr-1" />
                                Attendance
                              </div>
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <div className="flex items-center justify-center">
                                <FaGraduationCap className="mr-1" />
                                Exam
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {selectedBatchDetail.students.map((student) => (
                            <tr
                              key={student.studentId}
                              className={`hover:bg-gray-50 ${getTableRowColor(
                                student.overallProgress
                              )}`}
                            >
                              <td className="px-6 py-4 whitespace-nowrap sticky left-0 bg-white z-10">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {student.studentName}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {student.studentEmail}
                                  </div>
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <span
                                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                                    student.overallProgress >= 80
                                      ? "bg-emerald-100 text-emerald-800"
                                      : student.overallProgress >= 60
                                      ? "bg-blue-100 text-blue-800"
                                      : student.overallProgress >= 40
                                      ? "bg-amber-100 text-amber-800"
                                      : "bg-rose-100 text-rose-800"
                                  }`}
                                >
                                  {formatProgress(student.overallProgress)}%
                                </span>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <div className="text-sm font-semibold text-gray-900">
                                  {student.chaptersCompleted}/
                                  {student.chaptersTotal}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatProgress(student.chaptersProgress)}%
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <div className="text-sm font-semibold text-gray-900">
                                  {student.quizzesCompleted}/
                                  {student.quizzesTotal}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatProgress(student.quizProgress)}%
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <div className="text-sm font-semibold text-gray-900">
                                  {student.homeworkCompleted}/
                                  {student.homeworkTotal}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatProgress(student.homeworkProgress)}%
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <div className="text-sm font-semibold text-gray-900">
                                  {student.writingTasksCompleted}/
                                  {student.writingTasksTotal}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatProgress(student.writingTaskProgress)}%
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <div className="text-sm font-semibold text-gray-900">
                                  {student.classesAttended}/
                                  {student.classesTotal}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatProgress(student.attendanceProgress)}%
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                {student.examCompleted ? (
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                                    <FaCheckCircle className="mr-1" />
                                    Completed
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                                    Pending
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminProgressDashboard;
